package com.bikompanion.domain.usecase

import dev.zacsweers.metro.Inject
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.NetworkScopeCandidate
import com.bikompanion.domain.distanceKmBetween
import com.bikompanion.domain.ratingScopeIdOf

/**
 * How far a network's nearest known ground may be before it stops being a plausible answer.
 *
 * Deliberately far. On the active data source [BikeNetwork.coverage] is always null, so all
 * there is to measure against is the single point CityBikes publishes for the whole network —
 * which for a metro-wide operator can sit tens of kilometres from where a rider actually is.
 * A tight radius would silently drop the correct network; a loose one only ever adds an extra
 * entry to a list the rider is about to choose from anyway.
 */
const val NETWORK_SCOPE_THRESHOLD_KM = 50.0

/**
 * Ranks the networks that could plausibly own a bike at a given position.
 *
 * Ranking, not deciding: the result is ordered nearest-first and cut off at
 * [NETWORK_SCOPE_THRESHOLD_KM], and it is the caller's job to decide what to do with it. The
 * rule the UI applies is that exactly one candidate may be auto-selected (still overridable by
 * the rider) while two or more require an explicit pick — several networks in one city is the
 * normal case, not an error state.
 *
 * Never hard-filters on coverage. A network that publishes its docks is measured to the nearest
 * one; a network that publishes only a centre point is measured to that, and is not penalised
 * for the source being vague.
 *
 * One entry per *numbering scheme*, not per published network — see [collapseToScopes].
 */
@Inject
class ResolveNetworkScopeUseCase {
    operator fun invoke(
        networks: List<BikeNetwork>,
        latitude: Double,
        longitude: Double,
        thresholdKm: Double = NETWORK_SCOPE_THRESHOLD_KM,
    ): List<NetworkScopeCandidate> =
        networks
            .map { NetworkScopeCandidate(it, it.distanceKmFrom(latitude, longitude)) }
            .filter { it.distanceKm <= thresholdKm }
            .sortedBy { it.distanceKm }
            .collapseToScopes()

    /**
     * Folds the networks that share a numbering scheme into the one candidate they really are.
     *
     * Without this, standing in Barcelona under the CityBikes source produces seventeen
     * candidates — Bicing plus AMBici once per municipality, all named "Ambici" — which is both
     * an unanswerable question to put to a rider and a guarantee that their votes scatter across
     * ids. The nearest of a group represents it, keeping the ranking this list is built on, and
     * only its id is rewritten: everything else about it is still true of the scope as a whole.
     */
    private fun List<NetworkScopeCandidate>.collapseToScopes(): List<NetworkScopeCandidate> =
        groupBy { ratingScopeIdOf(it.network) }
            .map { (scopeId, sharingTheScope) ->
                val nearest = sharingTheScope.first()
                if (nearest.network.id == scopeId) {
                    nearest
                } else {
                    nearest.copy(network = nearest.network.copy(id = scopeId))
                }
            }.sortedBy { it.distanceKm }

    private fun BikeNetwork.distanceKmFrom(
        latitude: Double,
        longitude: Double,
    ): Double {
        val docks = coverage?.points
        if (docks.isNullOrEmpty()) {
            return distanceKmBetween(latitude, longitude, this.latitude, this.longitude)
        }
        return docks.minOf { distanceKmBetween(latitude, longitude, it.latitude, it.longitude) }
    }
}
